import React, { useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";

export default function ChangePasswordModal({ open, onClose, onChanged, email }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!current) { toast.error("Please enter your current password"); return; }
    if (next.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (next !== confirm) { toast.error("Passwords do not match"); return; }
    if (current === next) { toast.error("New password must be different from the current one"); return; }
    setBusy(true);
    try {
      const r = await api.post("/auth/change-password", { current_password: current, new_password: next });
      if (r.data?.token) {
        localStorage.setItem("calibrate_token", r.data.token);
      }
      toast.success("Password updated. Other sessions have been signed out.", { duration: 6000 });
      setCurrent(""); setNext(""); setConfirm("");
      onChanged?.();
      onClose?.();
    } catch (err) {
      toast.error(apiError(err, "Could not change password"));
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-start justify-center bg-black/40 backdrop-blur-sm p-4 sm:p-6 overflow-y-auto"
      onClick={onClose}
      data-testid="change-password-modal"
    >
      <div
        className="bg-[#F4F4F0] border border-[#111] cal-shadow w-full max-w-md mt-16 mb-10"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-[#111] px-5 py-3">
          <div>
            <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666]">[ACCOUNT]</div>
            <div className="font-display font-bold text-xl">Change password</div>
          </div>
          <button onClick={onClose} className="cal-btn cal-btn-ghost" data-testid="change-password-close" type="button"><X size={14}/></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div className="text-sm text-[#666] font-mono-cal">{email}</div>
          <div>
            <label className="cal-label">Current password</label>
            <input
              type="password" required autoFocus minLength={1}
              value={current} onChange={(e) => setCurrent(e.target.value)}
              className="cal-input"
              data-testid="change-password-current"
            />
          </div>
          <div>
            <label className="cal-label">New password (min 6)</label>
            <input
              type="password" required minLength={6}
              value={next} onChange={(e) => setNext(e.target.value)}
              className="cal-input"
              data-testid="change-password-new"
            />
          </div>
          <div>
            <label className="cal-label">Confirm new password</label>
            <input
              type="password" required minLength={6}
              value={confirm} onChange={(e) => setConfirm(e.target.value)}
              className="cal-input"
              data-testid="change-password-confirm"
            />
          </div>
          <p className="text-xs text-[#666]">All other devices will be signed out for security.</p>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="cal-btn cal-btn-ghost">Cancel</button>
            <button type="submit" disabled={busy} className="cal-btn" data-testid="change-password-submit">
              {busy ? "Saving…" : "Update password"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}
